/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import { NAME_CHANGE_CASES, NAME_CHANGE_STATUSES, type NameChangeCase, type NameChangeListItem, type NameChangeStatus, type PanelOperation } from '../../api/areas/namechange';
import { Badge, type Tone } from '../../components/Badge';
import { DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { selectClass } from '../operator/shared';
import { PanelExpected, PanelOperationActions, PanelStatusBadge } from './panel';
import { CheckField, useEnum } from './shared';

export const NC_STATUS_TONE: Record<NameChangeStatus, Tone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'pending',
  CORRECTION_REQUESTED: 'warn',
  APPROVED: 'info',
  REGISTRY_CONTACT: 'info',
  PANEL_PENDING: 'pending',
  COMPLETED: 'ok',
  REJECTED: 'crit',
  CANCELLED: 'neutral',
};

export function NameChangeStatusBadge({ status }: { status: NameChangeStatus }) {
  const e = useEnum();
  return <Badge tone={NC_STATUS_TONE[status]}>{e('ncStatus', status)}</Badge>;
}

/** `/name-changes`: "Đổi tên / chuyển nhượng" — requests and the VNNIC panel operations. */
export function NameChangesPage() {
  const { t } = useTranslation('services');
  const e = useEnum();
  const navigate = useNavigate();
  useDocumentTitle(t('nameChange.title'));
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState<NameChangeStatus | ''>('');
  const [kase, setKase] = useState<NameChangeCase | ''>('');
  const list = useApi((signal) => api.listNameChanges({ open: open || undefined, status: status || undefined, case: kase || undefined }, { signal }), [open, status, kase]);
  const panel = useApi((signal) => api.listPanelOperations({}, { signal }), []);
  const reload = () => {
    list.reload();
    panel.reload();
  };
  const cols: Column<NameChangeListItem>[] = [
    {
      key: 'domain',
      header: t('nameChange.col.domain'),
      cell: (r) => (
        <Link to={`/name-changes/${r.id}`} className="font-medium text-primary hover:underline">
          {r.domain}
        </Link>
      ),
    },
    { key: 'case', header: t('nameChange.col.case'), cell: (r) => <span className="text-sm">{e('ncCase', r.case_code)}{r.sub_case ? ` · ${e('ncSubCase', r.sub_case)}` : ''}</span> },
    { key: 'holders', header: t('nameChange.col.holders'), cell: (r) => <span className="text-xs">{r.from_holder_name ?? '—'} → {r.to_holder_name}</span> },
    { key: 'status', header: t('nameChange.col.status'), cell: (r) => <NameChangeStatusBadge status={r.status} /> },
    {
      key: 'due',
      header: t('nameChange.col.due'),
      className: 'whitespace-nowrap text-xs',
      cell: (r) => (r.review_due_at ? <span className={r.overdue ? 'font-medium text-critical' : undefined}>{formatDate(r.review_due_at)}{r.overdue ? ` · ${t('nameChange.overdue')}` : ''}</span> : '—'),
    },
    { key: 'panel', header: t('nameChange.col.panel'), cell: (r) => (r.panel_status ? <PanelStatusBadge status={r.panel_status} /> : '—') },
    { key: 'requested', header: t('nameChange.col.requested'), className: 'whitespace-nowrap text-xs', cell: (r) => `${formatDateTime(r.requested_at)} · ${r.requested_by_type}` },
  ];
  const panelCols: Column<PanelOperation>[] = [
    {
      key: 'domain',
      header: t('nameChange.col.domain'),
      cell: (o) =>
        o.source_type === 'NAME_CHANGE_REQUEST' ? (
          <Link to={`/name-changes/${o.source_id}`} className="font-medium text-primary hover:underline">
            {o.domain ?? o.domain_id}
          </Link>
        ) : (
          <Link to={`/domains/${encodeURIComponent(o.domain ?? o.domain_id)}/services`} className="font-medium text-primary hover:underline">
            {o.domain ?? o.domain_id}
          </Link>
        ),
    },
    { key: 'op', header: t('panel.col.operation'), cell: (o) => e('panelOperation', o.operation) },
    { key: 'expected', header: t('panel.col.expected'), cell: (o) => <PanelExpected op={o} /> },
    { key: 'status', header: t('panel.col.status'), cell: (o) => <PanelStatusBadge status={o.status} /> },
    { key: 'since', header: t('panel.col.since'), className: 'whitespace-nowrap text-xs', cell: (o) => `${formatDateTime(o.created_at)}${o.overdue_flagged_at ? ` · ${t('panel.overdue')}` : ''}` },
    { key: 'ref', header: t('panel.col.reference'), cell: (o) => <span className="text-xs">{o.submitted_reference ?? '—'}</span> },
    { key: 'actions', header: t('panel.col.actions'), cell: (o) => <PanelOperationActions op={o} onChanged={reload} /> },
  ];
  const openOps = panel.status === 'success' ? panel.data.items.filter((o) => o.status === 'PENDING' || o.status === 'SUBMITTED') : [];
  return (
    <>
      <PageHeader
        title={t('nameChange.title')}
        crumbs={[{ label: t('crumbs.business') }]}
        subtitle={t('nameChange.subtitle')}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        {list.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('nameChange.stats.toReview')} value={formatCount(list.data.items.filter((r) => r.status === 'SUBMITTED').length)} tone="pending" />
            <StatCard label={t('nameChange.stats.overdue')} value={formatCount(list.data.items.filter((r) => r.overdue).length)} tone={list.data.items.some((r) => r.overdue) ? 'crit' : 'ok'} />
            <StatCard label={t('nameChange.stats.panel')} value={formatCount(openOps.length)} tone={openOps.length ? 'warn' : 'ok'} />
          </StatGrid>
        ) : null}
        <Section title={t('nameChange.requests')} flush>
          <div className="px-5 pb-4">
            <FilterBar>
              <Field label={t('nameChange.filter.status')}>
                <select className={selectClass} value={status} onChange={(ev) => setStatus(ev.target.value as NameChangeStatus | '')}>
                  <option value="">{t('nameChange.filter.any')}</option>
                  {NAME_CHANGE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {e('ncStatus', s)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('nameChange.filter.case')}>
                <select className={selectClass} value={kase} onChange={(ev) => setKase(ev.target.value as NameChangeCase | '')}>
                  <option value="">{t('nameChange.filter.any')}</option>
                  {NAME_CHANGE_CASES.map((c) => (
                    <option key={c} value={c}>
                      {e('ncCase', c)}
                    </option>
                  ))}
                </select>
              </Field>
              <CheckField label={t('nameChange.filter.open')} checked={open} onChange={setOpen} />
            </FilterBar>
          </div>
          {list.status === 'error' ? <DataError error={list.error} title={t('common.loadFailed')} onRetry={list.reload} /> : null}
          {list.status === 'loading' ? <Loading rows={3} /> : null}
          {list.status === 'success' ? (
            <DataTable columns={cols} rows={list.data.items} rowKey={(r) => r.id} onRowClick={(r) => void navigate(`/name-changes/${r.id}`)} empty={<EmptyState title={t('nameChange.empty')} />} />
          ) : null}
        </Section>
        <Section title={t('panel.title')} description={t('panel.subtitle')} flush>
          {panel.status === 'error' ? <DataError error={panel.error} title={t('common.loadFailed')} onRetry={panel.reload} /> : null}
          {panel.status === 'success' ? <DataTable columns={panelCols} rows={panel.data.items} rowKey={(o) => o.id} empty={<EmptyState title={t('panel.empty')} />} /> : <Loading rows={2} />}
        </Section>
      </div>
    </>
  );
}
